import React, { useEffect, useMemo, useRef, useState } from 'react';
import clone from 'lodash/clone';

import { AnimateMovingBar } from '@r1-frontend/ui-react/components/MovingBar';
import { Pre } from '@r1-frontend/ui-react/components/typography/formatting';
import { H4, H6 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { TArrayTwoOrMore } from '@r1-frontend/shared/types/Array';

import * as S from './styled';

export type TBaseSpeedLineItem = {
    id: number | string,
    speed: number | string,
    price?: number | string,

}
export type TBaseSpeedLineProps = {
    items: TArrayTwoOrMore<TBaseSpeedLineItem>,
    startDot?: number,
    onSelect: (indexOfSelectedElement: number) => void,
    width?: number,
    className?: string,

    experimentalModeForConstructor?: boolean,
}

const BaseSpeedLine = (
    {
        className,
        items,
        onSelect,
        startDot = 0,
        width,

        experimentalModeForConstructor = false,
    }: TBaseSpeedLineProps,
): JSX.Element => {
    const mainRef = useRef<HTMLDivElement>(null);
    const [calculatedWidth, setWidth] = useState<number>(0);
    const [selectedDot, setSelectedDot] = useState<number>(startDot);
    if (items.length < 2) {
        throw new Error('@r1-frontend/ui-react. The BaseSpeedLine component Error. The "items" argument must contain 2 or more elements');
    }

    const { firstItem, lastItem, leftOverItems, dotsCount, firstLastWidth } = useMemo<{
        firstItem: TBaseSpeedLineItem,
        lastItem: TBaseSpeedLineItem,
        leftOverItems: TBaseSpeedLineItem[],
        dotsCount: number,
        firstLastWidth: string,
    }>(() => {
        const clonedItems = clone(items);
        const dots = items.length;
        const percentageWidth = (100 / (dots - 1)) / 2;

        return {
            lastItem: clonedItems.splice(-1, 1)[0],
            firstItem: clonedItems.splice(0, 1)[0],
            leftOverItems: clonedItems,
            dotsCount: dots,
            firstLastWidth: dots === 2 ? 'auto' : `${percentageWidth + percentageWidth / 7.5}%`,
        };
    }, [items]);

    useEffect(() => {
        if (mainRef !== null && mainRef.current !== null) {
            setWidth(mainRef.current.offsetWidth);
        }
    }, [mainRef]);

    useEffect(() => {
        setSelectedDot(startDot);
    }, [startDot, items]);

    const handleSelectedDot = (dotNumber: number) => {
        setSelectedDot(dotNumber);
        onSelect(dotNumber);
    };

    return (
        <S.Wrapper ref={mainRef} className={className}>
            <S.HeaderWrapper>
                <H4 as={'span'} $withIndent={false}>{items[selectedDot]?.speed} Мбит/с</H4>
            </S.HeaderWrapper>
            <AnimateMovingBar dotsCount={dotsCount} onDotSelected={handleSelectedDot} startDot={startDot} viewBoxWidth={width || calculatedWidth}/>
            <S.Block style={{ width: width || calculatedWidth }}>
                <S.SpeedItem key={'BaseSpeedLine_speed_firstItem'} style={{ width: firstLastWidth }}>
                    <H4 as={'p'} $withIndent={false}>{firstItem.speed}</H4>
                    <Pre $withIndent={false}>
                        <Paragraph4 $smaller>Мбит/с</Paragraph4>
                    </Pre>
                    <H6 as={'span'}>{firstItem.speed}</H6>
                </S.SpeedItem>
                <S.SpeedItem key={'BaseSpeedLine_speed_lastItem'} style={{ width: firstLastWidth }}>
                    <H4 as={'p'} $withIndent={false}>{lastItem.speed}</H4>
                    <Pre $withIndent={false}>
                        <Paragraph4 $smaller>Мбит/с</Paragraph4>
                    </Pre>
                    <H6 as={'span'}>{lastItem.speed}</H6>
                </S.SpeedItem>
                <S.SpeedsWrapper $isOdd={experimentalModeForConstructor && Boolean(dotsCount % 2)}>
                    {leftOverItems.map(item => <S.SpeedItem key={`BaseSpeedLine_speed_${item.id}`}>
                        <H4 as={'p'} $withIndent={false}>{item.speed}</H4>
                        <Pre $withIndent={false}>
                            <Paragraph4 $smaller>Мбит/с</Paragraph4>
                        </Pre>
                        <H6 as={'span'}>{item.speed}</H6>
                    </S.SpeedItem>)}
                    {leftOverItems.length === 0 ? <>&nbsp;</> : ''}
                </S.SpeedsWrapper>
            </S.Block>
            <S.Clearfix/>
        </S.Wrapper>
    );
};

export default BaseSpeedLine;
